import { getInitiativeById } from '@infosaas/shared'
import { useQuery } from '@tanstack/react-query'
import { createContext, useContext } from 'react'
import { useParams } from 'react-router-dom'
import { useClientProject } from '../../modules/dashboard/hooks/useClientProject'
import { FullPageLoader, FullPageMessage } from '../components/FullPageState'
import { supabase } from '../lib/supabase'
import { ProjectContext } from '../project/ProjectContext'

const InitiativeContext = createContext(null)

const FullPageSpinner = () => <FullPageLoader label="Carregando iniciativa..." />
const FullPageError = ({ title, message }) => <FullPageMessage title={title} message={message} />

/**
 * Carrega a initiative pelo `iid` da URL e, em paralelo, o projeto
 * via `initiative.project_id`. Expoe ambos via `useInitiative()`.
 *
 * O `useClientProject` ja eh memoizado pelo React Query, entao se o
 * mesmo projeto ja estiver em cache (ex: vindo do Dashboard via card),
 * nao refaz fetch.
 */
export function InitiativeProvider({ children }) {
  const { iid } = useParams()
  const initiativeId = Number(iid)

  const initiativeQuery = useQuery({
    queryKey: ['initiative-by-id', initiativeId],
    queryFn: () => getInitiativeById(supabase, initiativeId),
    enabled: Number.isFinite(initiativeId),
    staleTime: 1000 * 60 * 5,
  })

  const projectId = initiativeQuery.data?.project_id
  const projectQuery = useClientProject(projectId)

  if (initiativeQuery.isLoading || projectQuery.isLoading) return <FullPageSpinner />
  if (initiativeQuery.error) {
    return (
      <FullPageError
        title="Falha ao carregar a iniciativa"
        message={initiativeQuery.error.message ?? 'Tente recarregar a página.'}
      />
    )
  }
  if (!initiativeQuery.data) {
    return (
      <FullPageError
        title="Iniciativa não encontrada"
        message="Esta iniciativa não existe ou você não tem acesso a ela."
      />
    )
  }
  if (projectQuery.error || !projectQuery.data) {
    return (
      <FullPageError
        title="Falha ao carregar o projeto da iniciativa"
        message={projectQuery.error?.message ?? 'Projeto não encontrado.'}
      />
    )
  }

  const value = {
    initiative: initiativeQuery.data,
    initiativeId,
    project: projectQuery.data,
    projectId: projectQuery.data.id,
  }

  // Tambem preenche o ProjectContext para que componentes que usam
  // useProject() funcionem dentro de rotas /iniciativa/:iid sem refactor.
  return (
    <ProjectContext.Provider
      value={{ project: projectQuery.data, projectId: projectQuery.data.id }}
    >
      <InitiativeContext.Provider value={value}>{children}</InitiativeContext.Provider>
    </ProjectContext.Provider>
  )
}

/**
 * Hook obrigatorio dentro de InitiativeProvider.
 */
export function useInitiative() {
  const ctx = useContext(InitiativeContext)
  if (!ctx) {
    throw new Error('useInitiative() chamado fora de <InitiativeProvider>')
  }
  return ctx
}

/**
 * Hook opcional - retorna o contexto se existir, null caso contrario.
 * Util em componentes compartilhados entre rotas /projeto/:id e /iniciativa/:iid.
 */
export function useInitiativeOptional() {
  return useContext(InitiativeContext)
}
